/**
 * Search Keywords Utility
 * 
 * Shared utility for generating searchKeywords arrays from text fields
 * for Firestore array-contains-any queries. Used across backend (seeding,
 * RSS processing) and mobile app (entity creation).
 */

/**
 * Stop words for Portuguese, English, and Spanish
 * These common words are filtered out to improve search relevance
 */
const STOP_WORDS = new Set([
  // English
  'the', 'a', 'an', 'and', 'or', 'but', 'in', 'on', 'at', 'to', 'for',
  'of', 'with', 'by', 'from', 'up', 'about', 'into', 'through', 'during',
  'before', 'after', 'above', 'below', 'between', 'among', 'is', 'are',
  'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had', 'do', 'does',
  'did', 'will', 'would', 'could', 'should', 'may', 'might', 'must', 'can',
  // Portuguese
  'o', 'a', 'os', 'as', 'um', 'uma', 'uns', 'umas', 'de', 'da', 'do',
  'das', 'dos', 'em', 'no', 'na', 'nos', 'nas', 'por', 'para', 'com',
  'sem', 'sob', 'sobre', 'ao', 'aos', 'à', 'às', 'e', 'ou', 'mas',
  'que', 'se', 'como', 'quando', 'onde', 'quem', 'qual', 'quais',
  'seu', 'sua', 'seus', 'suas', 'este', 'esta', 'estes', 'estas',
  'esse', 'essa', 'esses', 'essas', 'aquele', 'aquela', 'aqueles', 'aquelas',
  'ser', 'estar', 'ter', 'haver', 'fazer', 'ir', 'vir', 'dar', 'ver',
  'sao', 'foi', 'foram', 'tem', 'ha', 'faz', 'vai', 'vem', 've',
  'mais', 'menos', 'muito', 'muitos', 'pouco', 'poucos', 'todo', 'todos',
  'isto', 'isso', 'aquilo', 'nao', 'nem', 'ja', 'so', 'tambem',
  // Spanish
  'el', 'la', 'los', 'las', 'un', 'una', 'unos', 'unas', 'del', 'al',
  'en', 'con', 'sin', 'por', 'para', 'sobre', 'entre', 'hasta', 'desde',
  'cuando', 'donde', 'como', 'que', 'cual', 'quien', 'cuanto',
  'este', 'esta', 'estos', 'estas', 'ese', 'esa', 'esos', 'esas',
  'aquel', 'aquella', 'aquellos', 'aquellas', 'mi', 'tu', 'su',
  'ser', 'estar', 'haber', 'tener', 'hacer', 'ir', 'poder', 'decir',
  'es', 'esta', 'fue', 'fueron', 'tiene', 'tienen', 'hay', 'hace', 'va', 'puede', 'dice',
  'mas', 'menos', 'muy', 'mucho', 'muchos', 'poco', 'pocos', 'todo', 'todos',
  'tambien', 'tampoco', 'si', 'no', 'ya', 'solo',
]);

/**
 * Normalize text by removing accents and converting to lowercase
 * 
 * @param text - The text to normalize
 * @returns Normalized text without accents in lowercase
 */
export function normalizeText(text: string): string {
  return text
    .normalize('NFD') // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .toLowerCase();
}

/**
 * Generate search keywords from text for Firestore array-contains-any queries
 * 
 * This function:
 * - Removes accents and converts to lowercase
 * - Filters out stop words (Portuguese, English, Spanish)
 * - Removes words shorter than 3 characters
 * - Removes pure numbers
 * - Deduplicates keywords
 * - Limits to maximum number of keywords
 * 
 * @param text - The text to extract keywords from
 * @param maxKeywords - Maximum number of keywords to return (default: 30)
 * @returns Array of unique normalized keywords
 * 
 * @example
 * ```typescript
 * const keywords = generateSearchKeywords('Forest Management Best Practices');
 * // Returns: ['forest', 'management', 'best', 'practices']
 * ```
 */
export function generateSearchKeywords(text: string, maxKeywords: number = 30): string[] {
  if (!text || typeof text !== 'string') {
    return [];
  }

  const normalized = normalizeText(text);
  
  // Extract words (alphanumeric sequences)
  const words = normalized
    .replace(/[^\w\s]/g, ' ') // Replace punctuation with spaces
    .split(/\s+/) // Split on whitespace
    .filter((word) => {
      // Filter out:
      // - Words shorter than 3 characters
      // - Stop words
      // - Pure numbers
      return (
        word.length >= 3 &&
        !STOP_WORDS.has(word) &&
        !/^\d+$/.test(word)
      );
    });

  // Deduplicate and limit
  const uniqueWords = Array.from(new Set(words));
  return uniqueWords.slice(0, maxKeywords);
}

/**
 * Generate search keywords from multiple text fields
 * Useful when combining title + content or multiple searchable fields
 * 
 * @param fields - Array of text fields to combine (null/undefined values ignored)
 * @param maxKeywords - Maximum number of keywords to return (default: 30)
 * @returns Array of unique normalized keywords
 * 
 * @example
 * ```typescript
 * const keywords = generateSearchKeywordsFromFields([
 *   article.title,
 *   article.content,
 *   article.summary
 * ]);
 * ```
 */
export function generateSearchKeywordsFromFields(
  fields: (string | undefined | null)[],
  maxKeywords: number = 30
): string[] {
  const combinedText = fields
    .filter((field): field is string => Boolean(field))
    .join(' ');
  
  return generateSearchKeywords(combinedText, maxKeywords);
}

/**
 * Generate normalized keywords from a user-supplied search query for Firestore
 * array-contains-any queries. This is the canonical query-time counterpart to
 * generateSearchKeywords (which is used at write/index time).
 *
 * Differences from generateSearchKeywords:
 * - Does NOT filter stop words (short queries like "não sei" would return nothing)
 * - Default max is 10 (Firestore array-contains-any hard limit)
 * - Intended for runtime query building only, not for storing keywords on documents
 *
 * @param searchQuery - The raw text typed by the user
 * @param maxKeywords - Maximum number of keywords to return (default: 10)
 * @returns Array of unique normalized keywords, capped at maxKeywords
 *
 * @example
 * ```typescript
 * const keywords = generateQueryKeywords('Eucalípto portugal');
 * // Returns: ['eucalipto', 'portugal']
 * ```
 */
export function generateQueryKeywords(searchQuery: string, maxKeywords: number = 10): string[] {
  if (!searchQuery || typeof searchQuery !== 'string') {
    return [];
  }

  const words = normalizeText(searchQuery)
    .replace(/[^\w\s]/g, ' ')
    .split(/\s+/)
    .filter((word) => word.length > 2);

  return Array.from(new Set(words)).slice(0, maxKeywords);
}
